"use client";

import { Call, Chat, Quickreply } from "@mui/icons-material";
import { Box, Portal, SpeedDial, useTheme } from "@mui/material";
import SpeedDialAction from "@mui/material/SpeedDialAction";

import React, { useContext, useEffect, useRef, useState } from "react";

import { PATH } from "@/configs/router/routerConfig";
import { ChatAIContext } from "@/contexts/ChatAIContext";
import { getViewportSize } from "@/utils/browserMetrics";
import { getPopupChatHeight } from "@/utils/chatAskCaddyHelpers";
// import { logger } from "@/lib/logger";
import { useTranslation } from "next-i18next";
import dynamic from "next/dynamic";
import { useRouter } from "next/router";
import { Resizable } from "re-resizable";

// Conversational UI (CUI) / In-App Messaging anatomy (terminology):
// - Launcher: the floating button in the bottom-right.
// - Widget: the UI that opens when the launcher is clicked.
// - Greeting: a proactive message (not implemented in this legacy component).

export type chatBoxOption = "popup" | "large" | "medium" | "hidden";
type Props = {
  chatBoxOption?: chatBoxOption;
  onChangeHeight: (height: number) => void;
};

type LauncherPosition = {
  right: number;
  bottom: number;
};

const LAUNCHER_STORAGE_KEY_V2 = "sellpath.launcher.position.v2";
const LAUNCHER_STORAGE_KEY_V1 = "sellpath.launcher.position.v1";
const LAUNCHER_SIZE_PX = 72;
const LAUNCHER_MARGIN_PX = 20;
export const ASSISTANT_WIDGET_Z_INDEX = 1290;
const DRAG_START_THRESHOLD_PX = 6;
const ApplicationsMessenger = dynamic(() => import("./Message"), { ssr: false });

const getViewportMetrics = () => {
  const vw = window.visualViewport?.width ?? document.documentElement.clientWidth;
  const vh = window.visualViewport?.height ?? document.documentElement.clientHeight;
  const offsetLeft = window.visualViewport?.offsetLeft ?? 0;
  const offsetTop = window.visualViewport?.offsetTop ?? 0;
  return { vw, vh, offsetLeft, offsetTop };
};

const clampLauncherPos = (pos: LauncherPosition): LauncherPosition => {
  const { vw, vh } = getViewportMetrics();

  const maxRight = Math.max(LAUNCHER_MARGIN_PX, vw - LAUNCHER_SIZE_PX - LAUNCHER_MARGIN_PX);
  const maxBottom = Math.max(LAUNCHER_MARGIN_PX, vh - LAUNCHER_SIZE_PX - LAUNCHER_MARGIN_PX);

  const rawRight = Number.isFinite(pos.right) ? pos.right : LAUNCHER_MARGIN_PX;
  const rawBottom = Number.isFinite(pos.bottom) ? pos.bottom : LAUNCHER_MARGIN_PX;

  return {
    right: Math.min(Math.max(LAUNCHER_MARGIN_PX, rawRight), maxRight),
    bottom: Math.min(Math.max(LAUNCHER_MARGIN_PX, rawBottom), maxBottom)
  };
};

const convertV1ToV2 = (v1: { x: number; y: number }): LauncherPosition => {
  const { vw, vh, offsetLeft, offsetTop } = getViewportMetrics();
  const right = offsetLeft + vw - v1.x - LAUNCHER_SIZE_PX;
  const bottom = offsetTop + vh - v1.y - LAUNCHER_SIZE_PX;
  return clampLauncherPos({ right, bottom });
};

function ChatBoxLegacy(props: Props) {
  const { t } = useTranslation();
  const router = useRouter();
  const { chatBoxOption = "hidden", onChangeHeight } = props;
  const {
    showChatbox,
    openChatbox,
    closeChatbox,
    isShowChatBoxTask,
    isOnboardingPending,
    showPersonalizationOverlay,
    setShowPersonalizationOverlay
  } = useContext(ChatAIContext);
  const theme = useTheme();
  const [currentHeight, setCurrentHeight] = useState<number>(350);
  const [constHeight, setConstHeight] = useState<number>(currentHeight);
  const [isExpand, setIsExpand] = useState<boolean>(false);
  const [launcherPos, setLauncherPos] = useState<LauncherPosition>(() => {
    if (typeof window === "undefined") {
      return { right: LAUNCHER_MARGIN_PX, bottom: LAUNCHER_MARGIN_PX };
    }
    return clampLauncherPos({ right: LAUNCHER_MARGIN_PX, bottom: LAUNCHER_MARGIN_PX });
  });

  const dragStateRef = useRef<{
    pointerId: number;
    startX: number;
    startY: number;
    originRight: number;
    originBottom: number;
    moved: boolean;
  } | null>(null);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    if (chatBoxOption === "popup") {
      const nextHeight = getPopupChatHeight(getViewportSize().height);
      setCurrentHeight((prev) => (prev === 350 ? nextHeight : prev));
      setConstHeight((prev) => (prev === 350 ? nextHeight : prev));
    }

    const onResize = () => {
      setLauncherPos((prev) => {
        const next = clampLauncherPos(prev);
        if (next.right === prev.right && next.bottom === prev.bottom) {
          return prev;
        }
        return next;
      });
    };

    onResize();
    window.addEventListener("resize", onResize);
    window.visualViewport?.addEventListener("resize", onResize);
    window.visualViewport?.addEventListener("scroll", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
      window.visualViewport?.removeEventListener("resize", onResize);
      window.visualViewport?.removeEventListener("scroll", onResize);
    };
  }, []);
  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    const fallback: LauncherPosition = clampLauncherPos({ right: LAUNCHER_MARGIN_PX, bottom: LAUNCHER_MARGIN_PX });

    try {
      const rawV2 = window.localStorage.getItem(LAUNCHER_STORAGE_KEY_V2);
      if (rawV2) {
        const parsed = JSON.parse(rawV2) as Partial<LauncherPosition>;
        const right = typeof parsed.right === "number" && Number.isFinite(parsed.right) ? parsed.right : fallback.right;
        const bottom =
          typeof parsed.bottom === "number" && Number.isFinite(parsed.bottom) ? parsed.bottom : fallback.bottom;
        setLauncherPos(clampLauncherPos({ right, bottom }));
        return;
      }

      const rawV1 = window.localStorage.getItem(LAUNCHER_STORAGE_KEY_V1);
      if (rawV1) {
        const parsedV1 = JSON.parse(rawV1) as Partial<{ x: number; y: number }>;
        if (
          typeof parsedV1.x === "number" &&
          Number.isFinite(parsedV1.x) &&
          typeof parsedV1.y === "number" &&
          Number.isFinite(parsedV1.y)
        ) {
          const v2 = convertV1ToV2({ x: parsedV1.x, y: parsedV1.y });
          setLauncherPos(v2);
          window.localStorage.setItem(LAUNCHER_STORAGE_KEY_V2, JSON.stringify(v2));
          return;
        }
      }

      setLauncherPos((prev) => clampLauncherPos(prev ?? fallback));
    } catch {
      setLauncherPos(fallback);
    }
  }, []);

  useEffect(() => {
    if (!isShowChatBoxTask) {
      setCurrentHeight(0);
      onChangeHeight(currentHeight);
    }
  }, [isShowChatBoxTask]);

  const styleResizable: React.CSSProperties = {
    display: (showChatbox && isShowChatBoxTask) || (chatBoxOption === "large" && isShowChatBoxTask) ? "block" : "none",
    background: theme.palette.mode === "dark" ? theme.sidebar.background : theme.sidebar.background,
    borderTop: `solid 1px ${theme.colors.alpha.black[50]}`
  };

  const handleExpandable = () => {
    const viewportHeight = getViewportSize().height;
    if (!isExpand) {
      setCurrentHeight(viewportHeight);
      setConstHeight(viewportHeight);
      setIsExpand(true);
    } else {
      setCurrentHeight(350);
      setConstHeight(350);
      setIsExpand(false);
    }
  };

  const shouldRenderMessenger = chatBoxOption !== "hidden" || showChatbox;

  const actions = [
    { icon: <Chat />, name: "Chat", onClick: () => openChatbox() },
    {
      icon: <Call />,
      name: "Call",
      onClick: () => {
        router.push(PATH.SALES_OPEN_ACTIVITIES);
        closeChatbox();
      }
    },
    {
      icon: <Quickreply />,
      name: "Quick Caddy",
      onClick: () => {
        router.push(PATH.QUICK_CADDY);
        closeChatbox();
      }
    }
  ];

  const handleLauncherPointerDown = (e: React.PointerEvent<HTMLElement>) => {
    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);
    dragStateRef.current = {
      pointerId: e.pointerId,
      startX: e.clientX,
      startY: e.clientY,
      originRight: launcherPos.right,
      originBottom: launcherPos.bottom,
      moved: false
    };
  };

  const handleLauncherPointerMove = (e: React.PointerEvent<HTMLElement>) => {
    const dragState = dragStateRef.current;
    if (!dragState || dragState.pointerId !== e.pointerId) {
      return;
    }

    const dx = e.clientX - dragState.startX;
    const dy = e.clientY - dragState.startY;
    if (!dragState.moved && Math.hypot(dx, dy) >= DRAG_START_THRESHOLD_PX) {
      dragState.moved = true;
    }

    const next = clampLauncherPos({ right: dragState.originRight - dx, bottom: dragState.originBottom - dy });
    setLauncherPos(next);
  };

  const handleLauncherPointerUp = (e: React.PointerEvent<HTMLElement>) => {
    const dragState = dragStateRef.current;
    if (!dragState || dragState.pointerId !== e.pointerId) {
      return;
    }
    dragStateRef.current = null;

    try {
      window.localStorage.setItem(LAUNCHER_STORAGE_KEY_V2, JSON.stringify(launcherPos));
    } catch {
      // ignore
    }
  };

  return (
    <>
      {chatBoxOption !== "hidden" && (
        <Box sx={{ transform: "translateZ(0px)", flexGrow: 1 }}>
          <Portal>
            <Box
              onClickCapture={(e) => {
                if (dragStateRef.current?.moved) {
                  e.preventDefault();
                  e.stopPropagation();
                }
              }}
              sx={{
                position: "fixed",
                right: `${launcherPos.right}px`,
                bottom: `${launcherPos.bottom}px`,
                zIndex: ASSISTANT_WIDGET_Z_INDEX - 1,
                touchAction: "none",
                WebkitUserSelect: "none",
                userSelect: "none",
                pointerEvents: "none"
              }}
            >
              <SpeedDial
                id="assistant-launcher"
                ariaLabel="Assistant Launcher"
                onClick={() => {
                  if (isOnboardingPending) {
                    setShowPersonalizationOverlay(!showPersonalizationOverlay);
                  } else {
                    openChatbox();
                  }
                }}
                FabProps={{
                  sx: {
                    width: LAUNCHER_SIZE_PX,
                    height: LAUNCHER_SIZE_PX,
                    borderRadius: "50%",
                    border: `3px solid ${theme.colors.alpha.white[100]}`,
                    background: "linear-gradient(225deg, black, #4E4E4E)",
                    touchAction: "none",
                    cursor: "grab",
                    "&:active": {
                      cursor: "grabbing"
                    },
                    "&:hover": {
                      background: "linear-gradient(225deg, #1a1a1a, #5E5E5E)",
                      boxShadow: `0 0 8px ${theme.colors.alpha.white[70]}`
                    }
                  },
                  onPointerDown: handleLauncherPointerDown,
                  onPointerMove: handleLauncherPointerMove,
                  onPointerUp: handleLauncherPointerUp,
                  onPointerCancel: handleLauncherPointerUp
                }}
                sx={{
                  position: "relative",
                  pointerEvents: "auto"
                }}
                icon={
                  <Box
                    component="img"
                    src="/Sellpath-logo.png"
                    alt="logo"
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: "50%"
                    }}
                  />
                }
              >
                {actions.map((action) => (
                  <SpeedDialAction
                    key={action.name}
                    icon={action.icon}
                    tooltipTitle={t(action.name)}
                    onClick={action.onClick}
                    sx={{
                      background: theme.colors.primary.main,
                      transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                      border: "2px solid transparent",
                      "&:hover": {
                        transform: "scale(1.08) translateY(-2px)",
                        boxShadow: `0 6px 20px ${theme.colors.alpha.black[25]}`,
                        background: theme.colors.primary.dark,
                        border: `4px solid ${theme.colors.secondary.main}`,
                        "& .MuiSvgIcon-root": {
                          transform: "rotate(5deg)"
                        }
                      },
                      "&:active": {
                        transform: "scale(0.95)",
                        transition: "transform 0.1s"
                      }
                    }}
                  />
                ))}
              </SpeedDial>
            </Box>
          </Portal>
        </Box>
      )}
      {shouldRenderMessenger && (
        <Resizable
          className="chatbox"
          onResize={(_, __, ___, delta) => {
            setCurrentHeight(constHeight + delta.height);
          }}
          onResizeStop={() => {
            setConstHeight(currentHeight);
          }}
          size={{
            height: `${currentHeight}px`,
            width: "100%"
          }}
          defaultSize={{
            height: "350px",
            width: "100%"
          }}
          style={styleResizable}
          minHeight={"30vh"}
          maxHeight={"100vh"}
          enable={{
            top: true,
            right: false,
            bottom: false,
            left: false
          }}
        >
          <ApplicationsMessenger
            chatBoxOption={chatBoxOption}
            handleExpandable={handleExpandable}
            isExpand={isExpand}
          />
        </Resizable>
      )}
    </>
  );
}

export default ChatBoxLegacy;
